// Queue service to manage payload submissions
import { enque_mongopayload } from '../components/Services';

const QUEUE_KEY = 'payload_queue';
const SESSION_KEY = 'queue_session_info';
const QUEUE_STATUS = {
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PAUSED: 'paused'
};

const FAILURE_TYPES = {
  NETWORK: 'network',
  AUTH: 'authentication',
  SERVER: 'server',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown'
};

class QueueService {
  constructor() {
    this.queue = this.loadQueue();
    this.processInterval = null;
    this.isOnline = navigator.onLine;
    this.sessionInfo = this.loadSessionInfo();
    this.isProcessing = false;
    this.maxRetries = 2;  // Reduced from 3
    this.baseRetryDelay = 2000; // 2 seconds
    this.maxRetryDelay = 30000; // 30 seconds
    this.batchSize = 3;  // Reduced from 5 - process fewer items at once
    this.maxQueueSize = 100;  // Reduced from unlimited - prevent queue growth
    this.processingFrequency = 10000;  // Increased from 5s - process less frequently
    this.initialize();
  }

  initialize() {
    // Set up network status monitoring
    window.addEventListener('online', () => {
      console.log('Network came back online');
      this.isOnline = true;
      this.resumeProcessing();
    });
    
    window.addEventListener('offline', () => {
      console.log('Network went offline');
      this.isOnline = false;
      this.pauseProcessing();
    });

    // Set up beforeunload handler to save state
    window.addEventListener('beforeunload', () => {
      this.handleBeforeUnload();
    });

    // Set up visibility change handler
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleAppResume();
      }
    });

    // Don't start processing automatically
    // Will be started manually when user is authenticated
  }
  
  startProcessing() {
    if (!this.processInterval && this.isOnline) {
      // Save current session info
      this.saveSessionInfo();
      
      // Clear any items from previous sessions that might be invalid
      this.validateExistingItems();
      
      // Cleanup old completed items to free memory
      this.cleanupCompletedItems();
      
      // Start processing the queue periodically with longer intervals
      this.processInterval = setInterval(() => {
        this.processQueue();
      }, this.processingFrequency);
      
      console.log('Queue processing started');
    } else if (!this.isOnline) {
      console.log('Queue processing not started: Offline');
    }
  }

  stopProcessing() {
    if (this.processInterval) {
      clearInterval(this.processInterval);
      this.processInterval = null;
      
      // Clean up queue items on stop to free memory
      this.cleanupCompletedItems();
      
      console.log('Queue processing stopped');
    }
  }

  /**
   * Remove completed/failed items from queue to prevent memory growth
   */
  cleanupCompletedItems() {
    const initialLength = this.queue.length;
    
    // Remove completed items (older than 5 minutes)
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    this.queue = this.queue.filter(item => {
      if (item.status === QUEUE_STATUS.COMPLETED) {
        const completedTime = new Date(item.completedAt || item.createdAt).getTime();
        return completedTime > fiveMinutesAgo; // Keep only recent completions
      }
      // Keep all non-completed items
      return true;
    });
    
    // Enforce max queue size - remove oldest items if queue is too large
    while (this.queue.length > this.maxQueueSize) {
      const oldestItem = this.queue.shift();  // Remove from front (oldest items)
      console.warn(`Queue overflow: Removed item ${oldestItem.id} to maintain max size`);
    }
    
    if (this.queue.length < initialLength) {
      this.saveQueue();
      console.log(`Queue cleanup: Removed ${initialLength - this.queue.length} items, size now: ${this.queue.length}`);
    }
  }

  pauseProcessing() {
    // Mark items as paused instead of stopping completely
    this.queue.forEach(item => {
      if (item.status === QUEUE_STATUS.IN_PROGRESS) {
        item.status = QUEUE_STATUS.PAUSED;
        item.pausedAt = new Date().toISOString();
      }
    });
    this.saveQueue();
    console.log('Queue processing paused');
  }

  resumeProcessing() {
    // Resume paused items
    this.queue.forEach(item => {
      if (item.status === QUEUE_STATUS.PAUSED) {
        item.status = QUEUE_STATUS.QUEUED;
        item.resumedAt = new Date().toISOString();
      }
    });
    this.saveQueue();
    
    // Restart processing if user is authenticated
    const apiToken = localStorage.getItem('api_token');
    if (apiToken && !this.processInterval) {
      this.startProcessing();
    }
    console.log('Queue processing resumed');
  }

  handleBeforeUnload() {
    // Save current state before page unload
    this.queue.forEach(item => {
      if (item.status === QUEUE_STATUS.IN_PROGRESS) {
        item.status = QUEUE_STATUS.QUEUED;
        item.interruptedAt = new Date().toISOString();
        item.interruptReason = 'browser_close';
      }
    });
    this.saveQueue();
    console.log('Queue state saved before unload');
  }

  handleAppResume() {
    // Handle app resumption (e.g., coming back from background)
    const currentTime = new Date().getTime();
    const sessionInfo = this.loadSessionInfo();
    
    if (sessionInfo && sessionInfo.lastActiveTime) {
      const timeDiff = currentTime - new Date(sessionInfo.lastActiveTime).getTime();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (timeDiff > fiveMinutes) {
        console.log('App resumed after long inactivity, validating queue items');
        this.validateExistingItems();
      }
    }
    
    // Update session info
    this.saveSessionInfo();
    
    // Resume processing if online and authenticated
    if (this.isOnline && localStorage.getItem('api_token')) {
      this.resumeProcessing();
    }
  }
  validateExistingItems() {
    // Check if existing queue items are still valid for current session
    const currentApiToken = localStorage.getItem('api_token');
    const currentUserId = localStorage.getItem('in_userid');
    const sessionInfo = this.loadSessionInfo();
    
    if (!currentApiToken || !currentUserId) {
      // If no valid session, clear all items
      this.queue = [];
      this.saveQueue();
      return;
    }
    
    // Check if session has changed
    if (sessionInfo && 
        (sessionInfo.apiToken !== currentApiToken || 
         sessionInfo.userId !== currentUserId)) {
      console.log('Session changed, clearing old queue items');
      this.queue = [];
      this.saveQueue();
      return;
    }
    
    // Update any in-progress or paused items back to queued since they were interrupted
    let recoveredItems = 0;
    this.queue.forEach(item => {
      if (item.status === QUEUE_STATUS.IN_PROGRESS || 
          item.status === QUEUE_STATUS.PAUSED) {
        item.status = QUEUE_STATUS.QUEUED;  // Converts back to queued
        item.updatedAt = new Date().toISOString();
        item.recoveredAt = new Date().toISOString();  // Marks as recovered
        recoveredItems++;
      }
    });
    
    if (recoveredItems > 0) {
      console.log(`Recovered ${recoveredItems} interrupted queue items`);
    }
    
    this.saveQueue();
  }

  loadQueue() {
    try {
      const savedQueue = localStorage.getItem(QUEUE_KEY);
      return savedQueue ? JSON.parse(savedQueue) : [];
    } catch (error) {
      console.error('Error loading queue from localStorage:', error);
      return [];
    }
  }

  saveQueue() {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      console.error('Error saving queue to localStorage:', error);
    }
  }

  loadSessionInfo() {
    try {
      const savedInfo = localStorage.getItem(SESSION_KEY);
      return savedInfo ? JSON.parse(savedInfo) : null;
    } catch (error) {
      console.error('Error loading session info:', error);
      return null;
    }
  }

  saveSessionInfo() {
    try {
      const sessionInfo = {
        apiToken: localStorage.getItem('api_token'),
        userId: localStorage.getItem('in_userid'),
        lastActiveTime: new Date().toISOString(),
        browserSession: sessionStorage.getItem('browser_session') || Date.now().toString()
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionInfo));
    } catch (error) {
      console.error('Error saving session info:', error);
    }
  }  addToQueue(payload, metadata = {}) {
    const queueItem = {
      id: `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      payload,
      metadata: {
        ...metadata,
        sessionId: (this.sessionInfo && this.sessionInfo.browserSession) || Date.now().toString(),
        addedBy: localStorage.getItem('in_userid'),
        userAgent: navigator.userAgent
      },
      status: QUEUE_STATUS.QUEUED,
      createdAt: new Date().toISOString(),
      retryCount: 0,
      maxRetries: this.maxRetries,
      error: null,
      lastRetryAt: null,
      nextRetryAt: null,
      failureType: null
    };
    
    this.queue.push(queueItem);
    this.saveQueue();
    
    console.log(`Added item to queue: ${queueItem.id}`);
    return queueItem.id;
  }

  calculateRetryDelay(retryCount) {
    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseRetryDelay * Math.pow(2, retryCount),
      this.maxRetryDelay
    );
    // Add jitter (±25%)
    const jitter = delay * 0.25 * (Math.random() * 2 - 1);
    return Math.max(1000, delay + jitter); // Minimum 1 second
  }

  classifyError(error) {
    if (!error) return FAILURE_TYPES.UNKNOWN;
    
    const errorMessage = error.toString().toLowerCase();
    
    if (errorMessage.includes('network') || 
        errorMessage.includes('fetch') ||
        errorMessage.includes('connection') ||
        !navigator.onLine) {
      return FAILURE_TYPES.NETWORK;
    }
    
    if (errorMessage.includes('401') || 
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('session invalid') ||
        errorMessage.includes('token')) {
      return FAILURE_TYPES.AUTH;
    }
    
    if (errorMessage.includes('500') || 
        errorMessage.includes('server error')) {
      return FAILURE_TYPES.SERVER;
    }
    
    if (errorMessage.includes('400') || 
        errorMessage.includes('validation') ||
        errorMessage.includes('invalid')) {
      return FAILURE_TYPES.VALIDATION;
    }
    
    return FAILURE_TYPES.UNKNOWN;
  }

  shouldRetry(item) {
    if (item.retryCount >= item.maxRetries) {
      return false;
    }
    
    // Don't retry validation errors immediately
    if (item.failureType === FAILURE_TYPES.VALIDATION) {
      return false;
    }
    
    // Check if enough time has passed for retry
    if (item.nextRetryAt && new Date() < new Date(item.nextRetryAt)) {
      return false;
    }
    
    return true;
  }

  updateItemStatus(id, status, error = null) {
    const item = this.queue.find(item => item.id === id);
    if (item) {
      item.status = status;
      item.error = error;
      item.updatedAt = new Date().toISOString();
      if (status === QUEUE_STATUS.FAILED) {
        item.retryCount += 1;
        item.lastRetryAt = new Date().toISOString();
      }
      this.saveQueue();
    }
    return item;
  }  async processQueue() {
    // Check if user is logged in before processing
    const apiToken = localStorage.getItem('api_token');
    const userId = localStorage.getItem('in_userid');
    
    if (!apiToken || !userId) {
      console.log('Queue processing skipped: User not authenticated');
      this.stopProcessing(); // Stop processing if not authenticated
      return;
    }

    // Check network connectivity
    if (!this.isOnline) {
      console.log('Queue processing skipped: Offline');
      return;
    }

    // Prevent concurrent processing
    if (this.isProcessing) {
      console.log('Queue processing already in progress');
      return;
    }

    this.isProcessing = true;

    try {
      // Get items that need processing
      const itemsToProcess = this.queue.filter(item => {
        return item.status === QUEUE_STATUS.QUEUED || 
               (item.status === QUEUE_STATUS.FAILED && this.shouldRetry(item));
      }).slice(0, this.batchSize); // Process in batches

      if (itemsToProcess.length === 0) {
        return;
      }

      console.log(`Processing ${itemsToProcess.length} queue items`);

      // Process items in parallel but with controlled concurrency
      const processPromises = itemsToProcess.map(item => this.processQueueItem(item));
      await Promise.allSettled(processPromises);

      // Clean up old completed items (keep only last 50)
      const completedItems = this.queue.filter(item => item.status === QUEUE_STATUS.COMPLETED);
      if (completedItems.length > 50) {
        const itemsToRemove = completedItems
          .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))
          .slice(0, completedItems.length - 50);
        
        itemsToRemove.forEach(item => {
          this.removeItem(item.id);
        });
      }

    } catch (error) {
      console.error('Error in queue processing:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async processQueueItem(item) {
    try {
      // Mark as in progress
      item.status = QUEUE_STATUS.IN_PROGRESS;
      item.updatedAt = new Date().toISOString();
      this.saveQueue();

      const { payload } = item;
      let response;      if (payload.action === 'enque_mongopayload') {
        // Use the existing service function
        console.log(`Processing enque_mongopayload for item ${item.id}`);
        response = await enque_mongopayload(
          payload.api_token,
          payload.db_name,
          payload.collection_name,
          payload.data
        );

        // Check the response from the service
        if (response.status === 1) {
          item.status = QUEUE_STATUS.COMPLETED;
          item.error = null;
          item.completedAt = new Date().toISOString();
          console.log(`Successfully processed item ${item.id}`);
        } else {
          throw new Error(response.message || 'Update failed');
        }
      } else {
        // Handle other queue actions if needed
        // For backwards compatibility with existing queue items
        const allowedKeys = ["object_id", "txn_ref_no", "status", "remark", "approver"];
        const payloadToSend = Object.fromEntries(
          Object.entries(payload)
            .filter(([key, value]) => allowedKeys.includes(key) && key !== "" && value !== undefined)
        );

        console.log(`Processing legacy payload for item ${item.id}:`, payloadToSend);

        const fetchResponse = await fetch(
          process.env.REACT_APP_URL + "/mongo/enqueue/callbacks/payment_staging",
          {
            method: "POST",
            headers: {
              "api-token": payload.api_token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payloadToSend),
          }
        );

        if (fetchResponse.status === 200) {
          item.status = QUEUE_STATUS.COMPLETED;
          item.error = null;
          item.completedAt = new Date().toISOString();
          console.log(`Successfully processed legacy item ${item.id}`);
        } else {
          const errorText = await fetchResponse.text();
          throw new Error(`HTTP ${fetchResponse.status}: ${errorText}`);
        }
      }

    } catch (error) {
      console.error(`Error processing item ${item.id}:`, error);
      
      // Classify the error
      item.failureType = this.classifyError(error);
      item.error = error.message;
      item.lastRetryAt = new Date().toISOString();

      // Handle different types of failures
      if (item.failureType === FAILURE_TYPES.AUTH) {
        console.log('Authentication error detected, stopping queue processing');
        item.status = QUEUE_STATUS.FAILED;
        this.stopProcessing();
      } else if (item.failureType === FAILURE_TYPES.NETWORK) {
        console.log('Network error detected, pausing item processing');
        item.status = QUEUE_STATUS.QUEUED; // Will retry when network is back
      } else if (this.shouldRetry(item)) {
        // Calculate next retry time
        const retryDelay = this.calculateRetryDelay(item.retryCount);
        item.nextRetryAt = new Date(Date.now() + retryDelay).toISOString();
        item.status = QUEUE_STATUS.FAILED;
        item.retryCount = (item.retryCount || 0) + 1;
        console.log(`Item ${item.id} will retry in ${Math.round(retryDelay / 1000)} seconds (attempt ${item.retryCount}/${item.maxRetries})`);
      } else {
        // Max retries reached or non-retryable error
        item.status = QUEUE_STATUS.FAILED;
        item.finalFailure = true;
        console.log(`Item ${item.id} failed permanently after ${item.retryCount} retries`);
      }
    } finally {
      item.updatedAt = new Date().toISOString();
      this.saveQueue();
    }
  }
  getQueueItems() {
    return this.queue;
  }

  getQueueSummary() {
    const summary = {
      total: this.queue.length,
      queued: 0,
      inProgress: 0,
      completed: 0,
      failed: 0,
      paused: 0,
      retryable: 0
    };

    this.queue.forEach(item => {
      summary[item.status] = (summary[item.status] || 0) + 1;
      
      if (item.status === QUEUE_STATUS.FAILED && this.shouldRetry(item)) {
        summary.retryable++;
      }
    });

    return summary;
  }

  getItemById(id) {
    return this.queue.find(item => item.id === id);
  }

  getFailedItems() {
    return this.queue.filter(item => item.status === QUEUE_STATUS.FAILED);
  }

  getRetryableItems() {
    return this.queue.filter(item => 
      item.status === QUEUE_STATUS.FAILED && this.shouldRetry(item)
    );
  }

  retryItem(id) {
    const item = this.getItemById(id);
    if (item && item.status === QUEUE_STATUS.FAILED && item.retryCount < item.maxRetries) {
      item.status = QUEUE_STATUS.QUEUED;
      item.nextRetryAt = null;
      item.error = null;
      item.updatedAt = new Date().toISOString();
      this.saveQueue();
      console.log(`Manually retrying item ${id}`);
      return true;
    }
    return false;
  }

  retryAllFailed() {
    const retryableItems = this.getRetryableItems();
    let retriedCount = 0;
    
    retryableItems.forEach(item => {
      if (this.retryItem(item.id)) {
        retriedCount++;
      }
    });
    
    console.log(`Retried ${retriedCount} failed items`);
    return retriedCount;
  }

  removeItem(id) {
    const initialLength = this.queue.length;
    this.queue = this.queue.filter(item => item.id !== id);
    this.saveQueue();
    
    if (this.queue.length < initialLength) {
      console.log(`Removed item ${id} from queue`);
      return true;
    }
    return false;
  }

  clearCompletedItems() {
    const initialLength = this.queue.length;
    this.queue = this.queue.filter(item => item.status !== QUEUE_STATUS.COMPLETED);
    this.saveQueue();
    
    const removedCount = initialLength - this.queue.length;
    console.log(`Cleared ${removedCount} completed items`);
    return removedCount;
  }

  clearAllItems() {
    const itemCount = this.queue.length;
    this.queue = [];
    this.saveQueue();
    console.log(`Cleared all ${itemCount} items from queue`);
    return itemCount;
  }

  clearFailedItems() {
    const initialLength = this.queue.length;
    this.queue = this.queue.filter(item => item.status !== QUEUE_STATUS.FAILED);
    this.saveQueue();
    
    const removedCount = initialLength - this.queue.length;
    console.log(`Cleared ${removedCount} failed items`);
    return removedCount;
  }

  isProcessingActive() {
    return this.processInterval !== null;
  }

  getProcessingStatus() {
    return {
      isActive: this.isProcessingActive(),
      isOnline: this.isOnline,
      isProcessing: this.isProcessing,
      sessionInfo: this.sessionInfo,
      queueSummary: this.getQueueSummary()
    };
  }

  destroy() {
    if (this.processInterval) {
      clearInterval(this.processInterval);
    }
  }
}

export const queueService = new QueueService();
export { QUEUE_STATUS };